import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import { Loader2, Mail, RefreshCw, UserPlus, Clock, Users, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { maskEmail } from '@ouropix/shared';
import { api } from '@/lib/api';
import { unwrapList, formatDateSafe, apiErrorMessage } from '@/lib/api-helpers';
import { TeamTable } from '@/components/admin-ops/TeamTable';
import { InviteAdminDialog } from '@/components/admin-ops/InviteAdminDialog';
import { CreateAdminAccountDialog } from '@/components/admin-ops/CreateAdminAccountDialog';
import { AllowedEmailsEditor } from '@/components/admin-ops/AllowedEmailsEditor';
import { BlockedEmailsEditor } from '@/components/admin-ops/BlockedEmailsEditor';

export const Route = createFileRoute('/_admin/equipe')({
  component: EquipePage,
});

interface PendingInvite {
  id: string;
  email: string;
  role: string;
  // Backend retorna camelCase (proto-loader keepCase=false), mas legacy front
  // tinha snake_case. Aceita ambos via fallback nos acessos abaixo.
  expires_at?: string;
  expiresAt?: string;
  created_at?: string;
  createdAt?: string;
  status?: string;
}

interface SettingRow {
  key: string;
  value: unknown;
}

function getRoleBadge(role: string) {
  const colors: Record<string, string> = {
    super_admin: 'bg-primary/10 text-primary border-primary/20',
    admin: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    moderator: 'bg-muted text-muted-foreground',
  };
  const labels: Record<string, string> = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    moderator: 'Moderador',
  };
  return (
    <Badge variant="outline" className={colors[role] ?? 'bg-muted text-muted-foreground'}>
      {labels[role] ?? role}
    </Badge>
  );
}

function EquipePage() {
  const queryClient = useQueryClient();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [createAccountOpen, setCreateAccountOpen] = useState(false);

  const { data: pendingInvites = [], isLoading: loadingInvites } = useQuery<PendingInvite[]>({
    queryKey: ['admin-pending-invites'],
    // Backend retorna { invites: [...], pagination: {...} }. unwrapList sem keys
    // fallback ('data','items','results','rows') nao acha 'invites' — passar
    // explicitamente. Sem isso, lista sempre aparecia vazia.
    queryFn: () =>
      // status=pending filtra accepted_at=null AND revoked_at=null no backend.
      // Sem isso, GET /admin/invites traz aceitos/revogados na mesma lista.
      api.get('/admin/invites', { params: { status: 'pending' } })
        .then((r) => unwrapList(r.data, 'invites')),
    retry: false,
    throwOnError: false,
  });

  // Settings keep loading mas allowedEmails passou pra AllowedEmailsEditor
  // (componente faz seu proprio fetch em /admin/allowed-emails).
  useQuery<SettingRow[]>({
    queryKey: ['admin-settings'],
    queryFn: () => api.get('/admin/settings').then((r) => unwrapList(r.data, 'settings')),
    retry: false,
    throwOnError: false,
  });

  const resendInviteMutation = useMutation({
    mutationFn: (inviteId: string) =>
      api.post(`/admin/invites/${inviteId}/resend`, {}).then((r) => r.data),
    onSuccess: () => {
      toast.success('Convite reenviado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['admin-pending-invites'] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Erro ao reenviar convite')),
  });

  const revokeInviteMutation = useMutation({
    mutationFn: (inviteId: string) =>
      api.delete(`/admin/invites/${inviteId}`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Convite revogado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['admin-pending-invites'] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Erro ao revogar convite')),
  });

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            Equipe
          </h1>
          <p className="text-muted-foreground text-sm">
            Gerencie os membros da equipe administrativa
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCreateAccountOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Criar Conta
          </Button>
          <Button onClick={() => setInviteDialogOpen(true)} className="gap-2">
            <UserPlus className="h-4 w-4" />
            Convidar Admin
          </Button>
        </div>
      </div>

      {/* Administradores Ativos */}
      <TeamTable />

      {/* Convites Pendentes */}
      <div className="bg-card rounded-lg border">
        <div className="p-4 border-b">
          <h2 className="font-semibold flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Convites Pendentes
          </h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Cargo</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Enviado em</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadingInvites ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : pendingInvites.length > 0 ? (
              pendingInvites.map((invite) => {
                const expiresStr = invite.expiresAt ?? invite.expires_at ?? '';
                const createdStr = invite.createdAt ?? invite.created_at ?? '';
                const expiresDate = expiresStr ? new Date(expiresStr) : null;
                const expiresValid = expiresDate && !Number.isNaN(expiresDate.getTime());
                const expired = expiresValid && expiresDate.getTime() < Date.now();
                return (
                <TableRow key={invite.id}>
                  <TableCell className="font-medium">{maskEmail(invite.email)}</TableCell>
                  <TableCell>{getRoleBadge(invite.role)}</TableCell>
                  <TableCell>
                    {expired ? (
                      <Badge variant="destructive" className="gap-1">
                        <Clock className="h-3 w-3" />
                        Expirado
                      </Badge>
                    ) : expiresValid ? (
                      <Badge variant="outline" className="gap-1 border-yellow-500/50 text-yellow-500">
                        <Clock className="h-3 w-3" />
                        Expira {formatDistanceToNow(expiresDate!, { addSuffix: true, locale: ptBR })}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1">
                        <Clock className="h-3 w-3" />
                        Sem expiração
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateSafe(createdStr, 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => resendInviteMutation.mutate(invite.id)}
                        disabled={resendInviteMutation.isPending}
                        title="Reenviar convite"
                      >
                        <RefreshCw className={`h-4 w-4 ${resendInviteMutation.isPending ? 'animate-spin' : ''}`} />
                      </Button>
                      <RevokeInviteButton
                        invite={invite}
                        onRevoke={() => revokeInviteMutation.mutate(invite.id)}
                        isPending={revokeInviteMutation.isPending}
                      />
                    </div>
                  </TableCell>
                </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Nenhum convite pendente
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* E-mails Permitidos */}
      <AllowedEmailsEditor />

      {/* Blacklist de E-mails e Domínios */}
      <BlockedEmailsEditor />

      <InviteAdminDialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen} />
      <CreateAdminAccountDialog open={createAccountOpen} onOpenChange={setCreateAccountOpen} />
    </div>
  );
}

function RevokeInviteButton({
  invite,
  onRevoke,
  isPending,
}: {
  invite: PendingInvite;
  onRevoke: () => void;
  isPending: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="text-destructive hover:text-destructive hover:bg-destructive/10"
        onClick={() => setOpen(true)}
        title="Revogar convite"
        aria-label="Revogar convite"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
      <ConfirmModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onConfirm={() => { onRevoke(); setOpen(false); }}
        title="Revogar Convite"
        description={
          <>
            Tem certeza que deseja revogar o convite de <strong>{maskEmail(invite.email)}</strong>?
            O link enviado deixará de funcionar.
          </>
        }
        confirmText="Revogar"
        cancelText="Cancelar"
        variant="destructive"
        isLoading={isPending}
      />
    </>
  );
}
